import ApiService from "../../services/ApiService";
import Logger from "../../services/Logger";

/** GET /v1/story/{id} sometimes returns `{ data: story }`; use inner record when outer is not story-shaped. */
function unwrapStoryGetResponse(response) {
    if (!response || typeof response !== "object") return response;
    const inner = response.data;
    if (!inner || typeof inner !== "object" || Array.isArray(inner)) return response;
    const outerIsStory =
        response.en != null ||
        response.fr != null ||
        typeof response.imageUrl === "string" ||
        typeof response.image_url === "string" ||
        typeof response.imagePrompt === "string" ||
        typeof response.image_prompt === "string";
    return outerIsStory ? response : inner;
}

const AdminStoryService = {
    /** GET /v1/story/stats — published counts by bucket (total, english, french, other). */
    getStats: async () => {
        try {
            const response = await ApiService.getData("/v1/story/stats");
            if (response && response.code === "FAILURE") {
                return {
                    success: false,
                    error: response.message || "Failed to fetch story stats",
                };
            }
            let payload = response;
            if (
                payload &&
                typeof payload === "object" &&
                payload.data != null &&
                typeof payload.data === "object" &&
                !Array.isArray(payload.data) &&
                "total" in payload.data
            ) {
                payload = payload.data;
            }
            return { success: true, data: payload };
        } catch (error) {
            Logger.error("Error fetching story stats:", error);
            return {
                success: false,
                error: error.message || "Failed to fetch story stats",
            };
        }
    },

    /**
     * Paginated stories — GET /v1/story
     * @param {number} page — 0-based
     * @param {number} pageSize
     * @param {string} sort — e.g. publishedAt:desc
     * @param {string} [language] — omit from URL when empty (all languages)
     * @param {string} [searchQuery] — search by name/title
     * @param {boolean} [standaloneImage] — true = standalone only, false = not standalone, omit = all
     * @param {string|boolean|null|undefined} [posted] — false = neither; both/true = both; instagram = Instagram only; facebook = Facebook only
     */
    list: async (page = 0, pageSize = 24, sort = "publishedAt:desc", language = "", searchQuery = "", standaloneImage, posted) => {
        try {
            const params = { page, pageSize, sort };
            const lang = typeof language === "string" ? language.trim() : "";
            if (lang) params.language = lang;
            
            const search = typeof searchQuery === "string" ? searchQuery.trim() : "";
            if (search) params.keyword = encodeURIComponent(search);
            if (standaloneImage === true) params.standaloneImage = "true";
            if (standaloneImage === false) params.standaloneImage = "false";
            if (posted === true || posted === "true" || posted === "both") params.posted = "both";
            else if (posted === false || posted === "false") params.posted = "false";
            else if (posted === "instagram" || posted === "facebook") params.posted = posted;

            const response = await ApiService.getData("/v1/story", params);
            if (response && response.code === "FAILURE") {
                return {
                    success: false,
                    error: response.message || "Failed to fetch stories",
                };
            }
            const content = response?.content ?? response?.data?.content ?? [];
            const list = Array.isArray(content) ? content : [];
            const totalElements =
                response?.totalElements ?? response?.page?.totalElements ?? list.length ?? 0;
            return {
                success: true,
                data: {
                    content: list,
                    totalElements: typeof totalElements === "number" ? totalElements : 0,
                    raw: response,
                },
            };
        } catch (error) {
            Logger.error("Error fetching stories:", error);
            return {
                success: false,
                error: error.message || "Failed to fetch stories",
            };
        }
    },

    /** GET /v1/story/{id} */
    getById: async (storyId) => {
        try {
            const response = await ApiService.getData(`/v1/story/${storyId}`);
            if (response && response.code === "FAILURE") {
                return { success: false, error: response.message || "Story not found" };
            }
            return { success: true, data: unwrapStoryGetResponse(response) };
        } catch (error) {
            Logger.error("Error fetching story:", error);
            return {
                success: false,
                error: error.message || "Failed to fetch story",
            };
        }
    },

    /** DELETE /v1/story/{id} */
    deleteById: async (storyId) => {
        try {
            if (!storyId) {
                return { success: false, error: "Story id required", data: null };
            }
            Logger.info("[STORY_DELETE] DELETE /v1/story/" + storyId);
            const response = await ApiService.deleteData(`/v1/story/${encodeURIComponent(storyId)}`);
            Logger.info("[STORY_DELETE] response", response);
            if (response && response.code === "FAILURE") {
                return {
                    success: false,
                    error: response.message || "Delete failed",
                    data: null,
                };
            }
            return { success: true, data: response };
        } catch (error) {
            Logger.error("Error deleting story:", error);
            return {
                success: false,
                error: error.response?.message || error.message || "Delete failed",
                data: null,
            };
        }
    },

    /** PUT /v1/story/{storyId}/high-priority-publish */
    setHighPriorityPublish: async (storyId, highPriorityPublish) => {
        try {
            if (!storyId) {
                return { success: false, error: "Story id required", data: null };
            }
            const response = await ApiService.putData(
                `/v1/story/${encodeURIComponent(storyId)}/high-priority-publish`,
                { highPriorityPublish: Boolean(highPriorityPublish) },
            );
            if (response && response.code === "FAILURE") {
                return {
                    success: false,
                    error: response.message || "Failed to prioritize Facebook and Instagram",
                    data: null,
                };
            }
            return { success: true, data: response };
        } catch (error) {
            Logger.error("Error prioritizing Facebook and Instagram:", error);
            return {
                success: false,
                error: error.response?.message || error.message || "Failed to prioritize Facebook and Instagram",
                data: null,
            };
        }
    },

    /**
     * POST /v1/story/{storyId}/publish-social — publish illustration to Facebook and Instagram now.
     * A channel that is already posted is left alone.
     */
    publishSocial: async (storyId) => {
        try {
            if (!storyId) {
                return { success: false, error: "Story id required", data: null };
            }
            const response = await ApiService.postData(
                `/v1/story/${encodeURIComponent(storyId)}/publish-social`,
                {},
            );
            if (response && response.code === "FAILURE") {
                return {
                    success: false,
                    error: response.message || "Social publish failed",
                    data: null,
                };
            }
            return { success: true, data: response };
        } catch (error) {
            Logger.error("Error publishing story to Facebook and Instagram:", error);
            return {
                success: false,
                error: error.response?.message || error.message || "Social publish failed",
                data: null,
            };
        }
    },

    /** POST /v1/story/gen-story — generate missing story text/image for eligible published templates */
    genStoryBatch: async (size) => {
        try {
            const n = clampStoryBatchSize(size);
            const response = await ApiService.postData(`/v1/story/gen-story?size=${n}`, {});
            if (response && response.code === "FAILURE") {
                return {
                    success: false,
                    error: response.message || "Batch gen-story failed",
                    data: null,
                };
            }
            return { success: true, data: response };
        } catch (error) {
            Logger.error("Error batch gen-story:", error);
            return {
                success: false,
                error: error.message || "Batch gen-story failed",
                data: null,
            };
        }
    },

    /** POST /v1/story/gen-image — build image_preview_url for eligible stories */
    genImageBatch: async (size) => {
        try {
            const n = clampStoryBatchSize(size);
            const response = await ApiService.postData(`/v1/story/gen-image?size=${n}`, {});
            if (response && response.code === "FAILURE") {
                return {
                    success: false,
                    error: response.message || "Batch gen-image failed",
                    data: null,
                };
            }
            return { success: true, data: response };
        } catch (error) {
            Logger.error("Error batch gen-image:", error);
            return {
                success: false,
                error: error.message || "Batch gen-image failed",
                data: null,
            };
        }
    },

    /** POST /v1/story/gen-image/{tplId} — preview (1200×630) + square icons from story image_url for this template */
    genImageForTemplate: async (tplId) => {
        try {
            if (!tplId) {
                return { success: false, error: "Template id required", data: null };
            }
            const response = await ApiService.postData(`/v1/story/gen-image/${encodeURIComponent(tplId)}`, {});
            if (response && response.code === "FAILURE") {
                return {
                    success: false,
                    error: response.message || "Generate image failed",
                    data: null,
                };
            }
            return { success: true, data: response };
        } catch (error) {
            Logger.error("Error gen-image for template:", error);
            return {
                success: false,
                error: error.message || "Generate image failed",
                data: null,
            };
        }
    },

    /**
     * POST /v1/story/{id}/gen-sliding — replaces sliding rows for this story with EASY/MEDIUM/HARD 12×12 templates.
     * Requires story image_url. Body: none (JSON `{}`).
     * @returns {{ success: true, data: SlidingRo[] } | { success: false, error: string }}
     */
    genSlidingForStory: async (storyId) => {
        try {
            if (!storyId) {
                return { success: false, error: "Story id required", data: null };
            }
            const response = await ApiService.postData(
                `/v1/story/${encodeURIComponent(storyId)}/gen-sliding`,
                {}
            );
            if (response && response.code === "FAILURE") {
                return {
                    success: false,
                    error: response.message || "Generate sliding puzzle failed",
                    data: null,
                };
            }
            if (Array.isArray(response)) {
                return { success: true, data: response };
            }
            if (response && typeof response === "object" && typeof response.message === "string") {
                return {
                    success: false,
                    error: response.message,
                    data: null,
                };
            }
            return {
                success: false,
                error: "Unexpected response from server",
                data: null,
            };
        } catch (error) {
            Logger.error("Error gen-sliding for story:", error);
            return {
                success: false,
                error:
                    error.response?.message ||
                    error.message ||
                    "Generate sliding puzzle failed",
                data: null,
            };
        }
    },

    /**
     * POST /v1/story/{id}/regen-image — generate a new main illustration and replace
     * imageUrl plus derived preview/icons. Does not rewrite story text.
     * @returns {{ success: true, data: object } | { success: false, error: string, data: null }}
     */
    regenImage: async (storyId) => {
        try {
            if (!storyId) {
                return { success: false, error: "Story id required", data: null };
            }
            const response = await ApiService.postData(
                `/v1/story/${encodeURIComponent(storyId)}/regen-image`,
                {}
            );
            if (response && response.code === "FAILURE") {
                return {
                    success: false,
                    error: response.message || "Regenerate image failed",
                    data: null,
                };
            }
            if (response && typeof response === "object" && typeof response.message === "string"
                && response.code && response.code !== "SUCCESS") {
                return {
                    success: false,
                    error: response.message,
                    data: null,
                };
            }
            return { success: true, data: unwrapStoryGetResponse(response) };
        } catch (error) {
            Logger.error("Error regen-image for story:", error);
            return {
                success: false,
                error:
                    error.response?.message ||
                    error.message ||
                    "Regenerate image failed",
                data: null,
            };
        }
    },

    /**
     * POST /v1/story/{id}/gen-sliding-jigsaw — creates sliding and/or jigsaw from the story's image_url
     * only for types that don't have rows yet. Safe to call more than once.
     * @returns {{ success: true, data: object } | { success: false, error: string }}
     */
    genSlidingJigsaw: async (storyId) => {
        try {
            if (!storyId) {
                return { success: false, error: "Story id required", data: null };
            }
            const response = await ApiService.postData(
                `/v1/story/${encodeURIComponent(storyId)}/gen-sliding-jigsaw`,
                {}
            );
            if (response && response.code === "FAILURE") {
                return {
                    success: false,
                    error: response.message || "Generate sliding/jigsaw failed",
                    data: null,
                };
            }
            if (response && typeof response === "object" && response.storyId) {
                return { success: true, data: response };
            }
            if (response && typeof response === "object" && typeof response.message === "string") {
                return {
                    success: false,
                    error: response.message,
                    data: null,
                };
            }
            return { success: true, data: response };
        } catch (error) {
            Logger.error("Error gen-sliding-jigsaw for story:", error);
            return {
                success: false,
                error:
                    error.response?.message ||
                    error.message ||
                    "Generate sliding/jigsaw failed",
                data: null,
            };
        }
    },

    /**
     * POST /v1/story/{id}/test-prompt-v2 - regenerate illustration into story-revision/
     * (does not update story).
     * @param {string} storyId
     * @param {string} [prompt] - when set, used as Stable Image prompt for either version
     * @param {string} [promptVersion] - "v1", "v2", "photo", or "standalone-v2" (6 painted pictures)
     * @param {string} [imageModel] - "ultra" (default) or "core"
     * @param {string} [artStyle] - art-medium override key (e.g. watercolor); "none" skips
     */
    testPromptV2: async (storyId, prompt, promptVersion = "v1", imageModel = "ultra", artStyle = "none") => {
        try {
            if (!storyId) {
                return { success: false, error: "Story id required", data: null };
            }
            const body = {};
            const promptText = typeof prompt === "string" ? prompt.trim() : "";
            if (promptText) body.prompt = promptText;
            const ver = typeof promptVersion === "string" ? promptVersion.trim() : "";
            if (ver) body.promptVersion = ver;
            const model = typeof imageModel === "string" ? imageModel.trim() : "";
            if (model) body.imageModel = model;
            const style = typeof artStyle === "string" ? artStyle.trim() : "";
            if (style) body.artStyle = style;
            const response = await ApiService.postData(
                `/v1/story/${encodeURIComponent(storyId)}/test-prompt-v2`,
                body
            );
            if (response && response.code === "FAILURE") {
                return {
                    success: false,
                    error: response.message || "test-prompt-v2 failed",
                    data: null,
                };
            }
            if (
                response &&
                typeof response === "object" &&
                (response.revisionImageUrl || response.revision_image_url)
            ) {
                return { success: true, data: response };
            }
            if (response && typeof response === "object" && typeof response.message === "string") {
                return { success: false, error: response.message, data: null };
            }
            return { success: true, data: response };
        } catch (error) {
            Logger.error("Error test-prompt-v2:", error);
            return {
                success: false,
                error: error.response?.message || error.message || "test-prompt-v2 failed",
                data: null,
            };
        }
    },

    /**
     * POST /v1/story/batch-disable-content — scan disabled/deleted jigsaw and sliding puzzles
     * and disable remaining published crossword, grid, sliding, and jigsaw for those stories.
     */
    batchDisableContent: async (size) => {
        try {
            const n = clampStoryBatchSize(size, 5000);
            const response = await ApiService.postData(`/v1/story/batch-disable-content?size=${n}`, {});
            if (response && response.code === "FAILURE") {
                return {
                    success: false,
                    error: response.message || "Batch disable content failed",
                    data: null,
                };
            }
            return { success: true, data: response };
        } catch (error) {
            Logger.error("Error batch disable content:", error);
            return {
                success: false,
                error: error.message || "Batch disable content failed",
                data: null,
            };
        }
    },
};

export function clampStoryBatchSize(size, max = 100) {
    const n = Number.parseInt(String(size), 10);
    if (!Number.isFinite(n)) return Math.min(10, max);
    return Math.min(max, Math.max(1, n));
}

export default AdminStoryService;
